<script>
  // The phones and computers that get the admin's push notifications: turn this one on or off, see the others, send a test.
  // Shared by the drops reminders and the new-order alerts; the server keeps one list of devices for both.
  import { onMount } from 'svelte';
  import Button from '../ui/Button.svelte';
  import { hintCls } from '../../lib/ui.js';
  import { toast } from '../../lib/toast.js';
  import { confirmDialog } from '../../lib/confirm.js';
  import { dropApi } from '../../lib/drops.js';
  import { pushSupport, currentSubscription, subscribePush, unsubscribePush } from '../../lib/push.js';
  import Smartphone from 'lucide-svelte/icons/smartphone';
  import Trash2 from 'lucide-svelte/icons/trash-2';
  import TriangleAlert from 'lucide-svelte/icons/triangle-alert';
  import LoaderCircle from 'lucide-svelte/icons/loader-circle';

  // `onEnabled` runs after this device signs up; `onLoaded` gets the device count whenever the list is read.
  let { onEnabled = () => {}, onLoaded = () => {} } = $props();

  let push = $state({ supported: false, reason: '', publicKey: '', keyNote: '', devices: [], error: '', loaded: false });
  let busy = $state('');
  const thisDevice = $derived(push.devices.find(d => d.current));

  async function refresh() {
    const sup = pushSupport();
    const sub = sup.ok ? await currentSubscription() : null;
    const r = await dropApi('POST', '/api/drops/push/status', { endpoint: sub?.endpoint || '' });
    push = { supported: sup.ok, reason: sup.reason, publicKey: r.publicKey, keyNote: r.keyNote || '', devices: r.devices, error: push.error, loaded: true };
    onLoaded(r.devices.length);
  }
  onMount(() => { refresh().catch((e) => toast.error(e.message)); });

  async function run(name, fn) {
    if (busy) return;
    busy = name;
    try { await fn(); } catch (e) { toast.error(e.message); } finally { busy = ''; }
  }
  const turnOn = () => run('on', async () => {
    push.error = '';
    try {
      const sub = await subscribePush(push.publicKey);                    // asks permission: runs straight from the click
      await dropApi('POST', '/api/drops/push/subscribe', { subscription: sub });
    } catch (e) { push.error = e.message; return; }                       // shown under the buttons: too long for a toast
    await refresh();
    toast.success('Notifications are on for this device.');
    onEnabled();
  });
  const turnOff = () => run('off', async () => { const endpoint = await unsubscribePush(); await dropApi('POST', '/api/drops/push/unsubscribe', { endpoint }); await refresh(); toast.success('Turned off for this device.'); });
  const remove = (d) => run('rm', async () => {
    if (!(await confirmDialog.ask({ title: `Remove ${d.label || 'this device'}?`, description: d.current ? 'It will stop getting notifications.' : 'It will stop getting notifications. It can sign up again from its own admin page.', confirmLabel: 'Remove', destructive: true }))) return;
    if (d.current) await unsubscribePush();
    await dropApi('DELETE', `/api/drops/push/devices/${d.id}`);
    await refresh();
  });
  const test = () => run('test', async () => { const r = await dropApi('POST', '/api/drops/push/test'); toast.success(`Sent to ${r.sent} of ${r.devices} device${r.devices === 1 ? '' : 's'}.`); await refresh(); });
</script>

<div class="space-y-3">
  {#if !push.loaded}
    <p class="flex items-center gap-2 text-sm text-muted-foreground" role="status"><LoaderCircle size={14} class="animate-spin" /> Loading…</p>
  {:else}
    {#if !push.supported}
      <p class="flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-200" role="status"><TriangleAlert size={15} class="mt-0.5 shrink-0" />{push.reason || 'This browser cannot receive notifications.'} You can still turn it on from another device.</p>
    {:else if thisDevice}
      <p class="text-sm text-emerald-700 dark:text-emerald-300" role="status">Notifications are on for this device.</p>
    {:else}
      <p class="text-sm text-muted-foreground">This device is not signed up yet.</p>
    {/if}
    {#if push.devices.length}
      <ul class="divide-y divide-border/60 rounded-lg border border-border" aria-label="Devices that get notifications">
        {#each push.devices as d (d.id)}
          <li class="flex items-center gap-3 px-3 py-2.5 text-sm">
            <Smartphone size={16} class="shrink-0 text-muted-foreground" />
            <span class="min-w-0 flex-1"><span class="block truncate font-medium">{d.label || 'Device'}{#if d.current}<span class="ml-1.5 font-normal text-muted-foreground">(this device)</span>{/if}</span>
              <span class="block text-xs text-muted-foreground">{d.lastOkAt ? `Last notified ${new Date(d.lastOkAt).toLocaleDateString('en-ZA', { day: 'numeric', month: 'short' })}` : 'Not notified yet'}</span></span>
            <Button variant="ghost" size="icon" aria-label="Remove {d.label || 'device'}" onclick={() => remove(d)} disabled={!!busy}><Trash2 size={15} /></Button>
          </li>
        {/each}
      </ul>
    {/if}
    <div class="flex flex-wrap gap-2">
      {#if push.supported && !thisDevice}<Button size="sm" onclick={turnOn} disabled={!!busy}>{#if busy === 'on'}<LoaderCircle size={14} class="animate-spin" />{/if} Turn on for this device</Button>{/if}
      {#if push.supported && thisDevice}<Button variant="outline" size="sm" onclick={turnOff} disabled={!!busy}>Turn off for this device</Button>{/if}
      {#if push.devices.length}<Button variant="outline" size="sm" onclick={test} disabled={!!busy}>{#if busy === 'test'}<LoaderCircle size={14} class="animate-spin" />{/if} Send a test notification</Button>{/if}
    </div>
    {#if push.error}<p role="alert" class="flex items-start gap-2 rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-red-900 dark:border-red-500/40 dark:bg-red-500/10 dark:text-red-200"><TriangleAlert size={15} class="mt-0.5 shrink-0" /><span>{push.error}</span></p>{/if}
    {#if push.keyNote}<p role="alert" class="flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-200"><TriangleAlert size={15} class="mt-0.5 shrink-0" /><span>{push.keyNote}</span></p>{/if}
    <p class={hintCls}>Do this on each phone or computer you want to be notified on. On an iPhone or iPad, add the admin to your Home Screen first.</p>
  {/if}
</div>
